import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type CompositionEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { TAMANHO_MAX_JOGADOR } from '@core/lexico/alfabeto';
import type { EstadoJogo } from '@core/motor/estados';
import type { Evento } from '@core/motor/eventos';

/**
 * O campo de texto real (§13, T01-02).
 *
 * Fica visualmente oculto, mas presente na árvore de acessibilidade e no foco:
 * é ele que abre o teclado virtual, que faz a composição de acentuadas
 * funcionar e que dá ao leitor de tela um `textbox` de verdade. O desenho das
 * letras acontece no tabuleiro; aqui só mora o estado do texto e do cursor.
 */
export function CampoDraft({
  estado,
  campoRef,
  despachar,
  aoMoverCaret,
  desabilitado,
  tecladoVirtual,
}: {
  estado: EstadoJogo;
  campoRef: RefObject<HTMLInputElement | null>;
  despachar: (evento: Evento) => void;
  aoMoverCaret: (posicao: number) => void;
  desabilitado: boolean;
  /** O jogo já mostra o próprio teclado: o do sistema não deve abrir (§14). */
  tecladoVirtual: boolean;
}) {
  /**
   * Composição em andamento (T01-02).
   *
   * Numa tecla morta — `´` e depois `e`, para dar `é` — o navegador deixa o
   * acento pendente dentro do campo e só depois o funde com a vogal. Esse
   * estado intermediário passa pelo `change`, e `filtrarEntrada` recusa o
   * acento sozinho (§4.1, com razão). O motor devolvia o texto sem ele, o
   * React reescrevia o valor do campo, e a composição morria ali: quem
   * digitava `férias` via `ferias`.
   *
   * Não basta ignorar o `change`. Um campo controlado é reescrito pelo React
   * depois de cada evento, e reescrevê-lo sem o acento pendente mata a
   * composição do mesmo jeito. Por isso, enquanto compõe, o `value` do campo
   * passa a ser o texto bruto do navegador — a reescrita vira uma cópia fiel,
   * e o motor só recebe o texto quando a composição fecha, já com a acentuada
   * pronta.
   */
  const compondo = useRef(false);
  const [bruto, setBruto] = useState<string | undefined>(undefined);

  const enviarAoMotor = useCallback(
    (campo: HTMLInputElement) => {
      despachar({ tipo: 'editar-draft', texto: campo.value });
      aoMoverCaret(campo.selectionStart ?? campo.value.length);
    },
    [despachar, aoMoverCaret],
  );

  const aoMudar = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (compondo.current) {
        setBruto(e.target.value);
        return;
      }
      enviarAoMotor(e.target);
    },
    [enviarAoMotor],
  );

  const aoComporInicio = useCallback((e: CompositionEvent<HTMLInputElement>) => {
    compondo.current = true;
    setBruto(e.currentTarget.value);
  }, []);

  const aoComporFim = useCallback(
    (e: CompositionEvent<HTMLInputElement>) => {
      compondo.current = false;
      setBruto(undefined);
      // O Chrome dispara o `input` final antes do `compositionend`; o Firefox,
      // depois. Enviar aqui cobre o primeiro, e o `change` que ainda venha do
      // segundo repete o mesmo texto — reenviar o mesmo draft não muda nada.
      enviarAoMotor(e.currentTarget);
    },
    [enviarAoMotor],
  );

  const aoTeclar = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const atalhoDeDesfazer =
        (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey;

      // §11: o atalho é sempre interceptado. O campo é um `<input>` de verdade
      // e tem histórico nativo: deixar o `Ctrl+Z` passar devolveria o texto
      // anterior pelo caminho do navegador, e o jogo teria dois desfazeres
      // discordando sobre o que é o passo anterior.
      if (atalhoDeDesfazer) {
        e.preventDefault();
        // T05-07: com draft não vazio o motor desfaz primeiro a edição do
        // texto; só com o campo vazio desfaz a jogada. Ver ADR-0006.
        despachar({ tipo: 'desfazer' });
        return;
      }

      // T06-04: alternar entre ocorrências válidas da âncora.
      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        despachar({ tipo: 'alternar-encaixe', direcao: e.key === 'ArrowLeft' ? -1 : 1 });
      }
    },
    [despachar],
  );

  const aoSelecionar = useCallback(
    (e: { currentTarget: HTMLInputElement }) => {
      aoMoverCaret(e.currentTarget.selectionStart ?? 0);
    },
    [aoMoverCaret],
  );

  const rotulo =
    estado.ancora !== undefined
      ? `Palavra que contenha a letra ${estado.ancora.caractere}`
      : 'Palavra horizontal';

  return (
    <>
      <label className="apenas-leitor" htmlFor="campo-draft">
        {rotulo}
      </label>
      <input
        id="campo-draft"
        ref={campoRef}
        className="campo-real"
        type="text"
        value={bruto ?? estado.draft}
        onChange={aoMudar}
        onCompositionStart={aoComporInicio}
        onCompositionEnd={aoComporFim}
        onKeyDown={aoTeclar}
        onSelect={aoSelecionar}
        disabled={desabilitado}
        maxLength={TAMANHO_MAX_JOGADOR}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        enterKeyHint="done"
        /* `none` mantém o campo focado, editável e com o cursor onde está,
           mas não faz o sistema abrir o teclado por cima do tabuleiro. */
        inputMode={tecladoVirtual ? 'none' : 'text'}
        /* A descrição aponta para a própria linha de feedback (§7.3), que já
           carrega a mensagem do estado. Duplicar o texto num span oculto
           colocaria a mesma frase duas vezes na árvore de acessibilidade. */
        aria-describedby="feedback-estado"
      />
    </>
  );
}
